import { Headshot } from './Headshot';
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTicketApi } from '../lib/ticketApi';
import { Loader2 } from 'lucide-react';
import { legRank, blinkClass } from './ticketDate';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const TONE = {
  green: 'border-emerald-400 bg-emerald-500/15 text-emerald-100',
  blue: 'border-sky-400 bg-sky-500/15 text-sky-100',
  purple: 'border-purple-400 bg-purple-500/20 text-purple-100',
  yellow: 'border-yellow-400 bg-yellow-500/10 text-yellow-100',
  red: 'border-red-500 bg-red-500/15 text-red-200',
  orange: 'border-orange-400 bg-orange-500/15 text-orange-100',
};
const STAT = { points: 'PTS', rebounds: 'REB', assists: 'AST', threes: '3PM', passing_yards: 'Pass yds', rushing_yards: 'Rush yds', receiving_yards: 'Rec yds',
  receptions: 'Rec', passing_tds: 'Pass TD', anytime_td: 'TD', pra: 'PRA', dd: 'Double-double', steals: 'STL', blocks: 'BLK' };
const fmt = (v) => (v == null ? '–' : Number.isInteger(Number(v)) ? Number(v) : Number(v).toFixed(1));

const Mark = ({ m }) => (
  <div className={`rounded-md border px-2 py-1 text-xs ${TONE[m.color] || 'border-zinc-700 bg-zinc-900 text-zinc-300'}${blinkClass(m.blink, m.color)}`} data-testid="game-player-mark" data-blink={m.blink ? 'true' : undefined}>
    <b>{STAT[m.stat] || m.stat}</b> {fmt(m.current)} / {m.side === 'under' ? 'under ' : ''}{fmt(m.target)}{m.side === 'under' ? '' : '+'}
    {m.remaining > 0 && <span className="opacity-80"> · needs {fmt(m.remaining)}</span>}
    {m.count > 1 && <span className="ml-1 font-black">×{m.count}</span>}
  </div>
);

const Row = ({ p }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-950/70 p-2 space-y-1.5" data-testid="game-player-row">
    <div className="flex items-center gap-2">
      {p.athlete_id && p.league === 'nba' && <Headshot leg={p} size={30} testId="game-player-headshot" />}
      {p.logo && <img src={p.logo} alt="" className="w-5 h-5 object-contain" data-testid="game-player-logo" />}
      <span className="flex-1 truncate text-sm font-bold text-white">{p.name}{p.jersey ? ` #${p.jersey}` : ''}</span>
      {p.tickets > 1 && <span className="text-[11px] font-black text-amber-300" data-testid="game-player-tickets">on {p.tickets} tickets</span>}
    </div>
    {p.availability && <p className="text-[11px] text-orange-300">⚠ {p.availability.text}</p>}
    <div className="flex flex-wrap gap-1.5">{[...p.marks].sort((a, b) => legRank({ blink: a.blink }, a.color) - legRank({ blink: b.blink }, b.color)).map((m) => <Mark key={`${m.stat}-${m.target}-${m.side}`} m={m} />)}</div>
  </div>
);

// Tap a live scoreboard → every player from that game on your tickets, repeats merged, one tab per team. Updates every 20 s.
export default function GamePlayersPanel({ g, side, onClose }) {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(side || 'away');
  const api = useTicketApi();
  const load = useCallback(() => axios.get(`${api.base}/active-scoreboard/${g.league}/${g.event_id}/players`, { headers: api.headers }).then((r) => setData(r.data)).catch(() => {}), [g.league, g.event_id, api]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { setTab(side || 'away'); }, [side]);
  const best = (p) => Math.min(...p.marks.map((m) => legRank({ blink: m.blink }, m.color)));
  const list = (data?.players || []).filter((p) => p.side === tab || (p.side === 'other' && tab === 'away')).sort((a, b) => best(a) - best(b));
  const count = (s) => (data?.players || []).filter((p) => p.side === s).length;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-zinc-950 border-zinc-800 text-white" data-testid="game-players-panel">
        <DialogHeader>
          <DialogTitle className="text-base">{g.away.abbr} {g.away.score ?? ''} @ {g.home.abbr} {g.home.score ?? ''} <span className="text-xs text-zinc-400">· {g.detail || ''}</span></DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-1.5">
          {['away', 'home'].map((s) => (
            <button key={s} type="button" onClick={() => setTab(s)} data-testid={`game-players-tab-${s}`}
              className={`flex items-center justify-center gap-1.5 rounded-lg px-2 py-2 text-sm font-bold ${tab === s ? 'bg-amber-500 text-black' : 'border border-zinc-700 text-zinc-300'}`}>
              {g[s].logo && <img src={g[s].logo} alt="" className="w-5 h-5 object-contain" />}{g[s].abbr} · {count(s)}
            </button>
          ))}
        </div>
        {!data ? <Loader2 className="mx-auto w-5 h-5 animate-spin text-zinc-400" /> : list.length ? (
          <div className="space-y-2" data-testid="game-players-list">{list.map((p) => <Row key={p.name} p={p} />)}</div>
        ) : <p className="text-sm text-zinc-400" data-testid="game-players-empty">No {g[tab].abbr} players on your open tickets.</p>}
        <p className="text-[10px] text-zinc-500">Updates every 20 s · yellow under 50% · purple 50% · blue 75% · green hit</p>
      </DialogContent>
    </Dialog>
  );
}
